import { EmptyState, ErrorState, LoadingState } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useLocale } from "~/i18n";
import { fetchMembershipInvites, revokeMembershipInvite } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type {
  MembershipInviteStatus,
  MembershipInviteSummary,
} from "~/types/users-admin";

const STATUS_COLOR: Record<
  MembershipInviteStatus,
  "default" | "success" | "warning"
> = {
  pending: "warning",
  claimed: "success",
  revoked: "default",
};

/** Pending and historical override invites, with one-way revocation for unused links. */
export function InvitationsPanel() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<MembershipInviteSummary | null>(
    null,
  );

  const invites = useQuery({
    queryKey: queryKeys.membershipInvites,
    queryFn: ({ signal }) => fetchMembershipInvites(signal),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeMembershipInvite(id),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.membershipInvites,
      });
    },
  });

  const dateOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  const closeConfirmation = () => {
    if (revoke.isPending) return;
    setConfirming(null);
    revoke.reset();
  };

  const beginRevoke = (invite: MembershipInviteSummary) => {
    revoke.reset();
    setConfirming(invite);
  };

  return (
    <Box component="section" sx={{ marginTop: { xs: 5, md: 7 } }}>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="H2" sx={{ color: "text.primary" }}>
          {t("invites.title")}
        </Typography>
        <Typography variant="T3" sx={{ color: "text.muted", marginTop: 0.5 }}>
          {t("invites.subtitle")}
        </Typography>
      </Box>

      {invites.isPending ? (
        <LoadingState />
      ) : invites.isError ? (
        <ErrorState onRetry={() => void invites.refetch()} />
      ) : invites.data.length === 0 ? (
        <EmptyState message={t("invites.empty")} />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table data-testid="invites.table" sx={{ minWidth: 780 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("invites.columns.email")}</TableCell>
                <TableCell>{t("invites.columns.status")}</TableCell>
                <TableCell>{t("invites.columns.createdAt")}</TableCell>
                <TableCell>{t("invites.columns.resolvedAt")}</TableCell>
                <TableCell align="right">
                  {t("invites.columns.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.data.map((invite) => (
                <TableRow
                  key={invite.id}
                  data-testid={`invites.row-${invite.id}`}
                >
                  <TableCell>
                    <Typography variant="L1" sx={{ color: "text.primary" }}>
                      {invite.email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={STATUS_COLOR[invite.status]}
                      label={t(`invites.status.${invite.status}`)}
                    />
                  </TableCell>
                  <TableCell>
                    <time dateTime={invite.created_at}>
                      {formatDate(invite.created_at, dateOptions)}
                    </time>
                  </TableCell>
                  <TableCell>
                    {invite.claimed_at || invite.revoked_at ? (
                      <time
                        dateTime={
                          invite.claimed_at ?? invite.revoked_at ?? undefined
                        }
                      >
                        {formatDate(
                          invite.claimed_at ?? invite.revoked_at ?? "",
                          dateOptions,
                        )}
                      </time>
                    ) : (
                      t("common.notSet")
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {invite.status === "pending" ? (
                      <Button
                        color="error"
                        size="small"
                        onClick={() => beginRevoke(invite)}
                        data-testid={`invites.revoke-${invite.id}`}
                      >
                        {t("invites.revoke")}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={confirming !== null} onClose={closeConfirmation}>
        <DialogTitle>{t("invites.revokeConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("invites.revokeConfirmBody", { email: confirming?.email })}
          </DialogContentText>
          {revoke.isError ? (
            <Alert
              severity="error"
              data-testid="invites.revoke-error"
              sx={{ marginTop: 2 }}
            >
              {t("invites.revokeError")}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmation} disabled={revoke.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => confirming && revoke.mutate(confirming.id)}
            disabled={revoke.isPending}
            data-testid="invites.revoke-confirm"
          >
            {t("invites.revoke")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
